import { useEffect, useState } from 'react'
import api from '../../api'
import '../../CSS/SchedularSettings.css'
const initial = {
  jobKey: 'AttendanceSynchronization',
  displayName: 'Attendance Synchronization',
  intervalMinutes: 5,
  isActive: true,
  firstExecution: 0,
  nextRunAtUtc: null,
  lastRunAtUtc: null,
  lastRunStatus: 'Loading',
  lastRunMessage: null
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
}

export default function SchedulerSettings() {
  const [config, setConfig] = useState(initial)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  // Uses the project's shared axios instance (same base URL/auth as every other screen).
  async function request(path, options = {}) {
    try {
      const res = await api.request({
        url: `/api/scheduler/${path}`,
        method: options.method || 'GET',
        data: options.data
      })
      return res.data ?? null
    } catch (e) {
      const body = e.response?.data
      const message = body?.errors
        ? Object.values(body.errors).flat().join(' ')
        : body?.message || body?.title || e.message
      throw new Error(message)
    }
  }

  async function load() {
    setLoading(true); setError('')
    try {
      const data = await request('configuration')
      if (!data) throw new Error(`Could not load configuration from the API. Check that the backend has the scheduler endpoints and is running.`)
      setConfig(data)
    }
    catch (e) { setError(e.message || 'Unable to load scheduler configuration.') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  // Live status: every 10 seconds, silently refresh ONLY the read-only run info
  // (next/last run, status, message). Unsaved edits in the form are never overwritten.
  useEffect(() => {
    const timer = setInterval(async () => {
      if (document.hidden) return
      try {
        const data = await request('configuration')
        if (!data) return
        setConfig(current => ({
          ...current,
          nextRunAtUtc: data.nextRunAtUtc,
          lastRunAtUtc: data.lastRunAtUtc,
          lastRunStatus: data.lastRunStatus,
          lastRunMessage: data.lastRunMessage
        }))
      } catch { /* ignore temporary errors; the next tick will retry */ }
    }, 10000)
    return () => clearInterval(timer)
  }, [])

  function update(field, value) {
    setConfig(current => ({ ...current, [field]: value }))
    setNotice('')
  }

  async function save(event) {
    event.preventDefault()
    setError(''); setNotice('')
    const interval = Number(config.intervalMinutes)
    if (!Number.isInteger(interval) || interval < 1 || interval > 1440) {
      setError('Interval must be a whole number between 1 and 1440 minutes.')
      return
    }
    setSaving(true)
    try {
      const result = await request('configuration', {
        method: 'PUT',
        data: ({
          jobKey: config.jobKey,
          displayName: config.displayName.trim(),
          intervalMinutes: interval,
          isActive: config.isActive,
          firstExecution: Number(config.firstExecution)
        })
      })
      setConfig(result)
      setNotice('Scheduler configuration saved successfully.')
    } catch (e) { setError(e.message || 'Unable to save configuration.') }
    finally { setSaving(false) }
  }

  async function runNow() {
    setError(''); setNotice(''); setRunning(true)
    try {
      const result = await request('run-now', { method: 'POST' })
      setNotice(result?.message || 'Job queued.')
      await load()
    } catch (e) { setError(e.message || 'Unable to queue job.') }
    finally { setRunning(false) }
  }

  if (loading) return <main className="page"><div className="panel">Loading scheduler configuration…</div></main>

  return (
    <main className="page">
      <header className="topbar">
        <div className="brand"><span className="brand-mark">P</span><div><strong>PROWESS <em>ERP</em></strong><small>Enterprise Scheduler</small></div></div>
        <span className={`state ${config.isActive ? 'enabled' : 'disabled'}`}><i />{config.isActive ? 'Scheduler Active' : 'Scheduler Inactive'}</span>
      </header>

      <section className="heading">
        <div className="eyebrow">SYSTEM ADMINISTRATION / AUTOMATION</div>
        <h1>Scheduler Configuration</h1>
        <p>Configure background jobs and control how often Prowess ERP runs them.</p>
      </section>

      {error && <div className="alert error" role="alert">{error}</div>}
      {notice && <div className="alert success" role="status">{notice}</div>}

      <div className="layout">
        <section className="panel form-panel">
          <div className="panel-title"><div><h2>Job settings</h2><p>Define the execution schedule for this ERP task.</p></div><span className="job-icon">↻</span></div>
          <form onSubmit={save}>
            <label htmlFor="job">Background job</label>
            <input id="job" value={config.displayName} readOnly />
            <small className="hint">Job key: {config.jobKey}</small>

            <div className="field-row">
              <div className="field">
                <label htmlFor="interval">Execution interval</label>
                <div className="interval-control">
                  <input id="interval" type="number" min="1" max="1440" step="1" required
                    value={config.intervalMinutes}
                    onChange={e => update('intervalMinutes', e.target.value)} />
                  <span>Minutes</span>
                </div>
                <small className="hint">Allowed range: 1–1440 minutes.</small>
              </div>
              <div className="field">
                <label htmlFor="firstExecution">First execution</label>
                <select id="firstExecution" value={config.firstExecution}
                  onChange={e => update('firstExecution', Number(e.target.value))}>
                  <option value={0}>Immediately</option>
                  <option value={1}>After interval</option>
                </select>
                <small className="hint">Applies when enabled or timing changes.</small>
              </div>
            </div>

            <div className="toggle-card">
              <div><strong>Enable scheduler</strong><p>Allow this job to run automatically.</p></div>
              <label className="switch" aria-label="Enable scheduler">
                <input type="checkbox" checked={config.isActive} onChange={e => update('isActive', e.target.checked)} />
                <span className="slider" />
              </label>
            </div>

            <div className="actions">
              <button className="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save configuration'}</button>
              <button className="secondary" type="button" onClick={load} disabled={saving}>Discard changes</button>
            </div>
          </form>
        </section>

        <aside className="side-column">
          <section className="panel summary-panel">
            <h2>Execution overview</h2>
            <div className="summary-item"><span>Job status</span><strong className={config.isActive ? 'green' : 'muted'}>{config.isActive ? 'Enabled' : 'Disabled'}</strong></div>
            <div className="summary-item"><span>Runs every</span><strong>{config.intervalMinutes || '—'} minute(s)</strong></div>
            <div className="summary-item"><span>Next scheduled run (local time)</span><strong>{formatDate(config.nextRunAtUtc)}</strong></div>
            <div className="summary-item"><span>Last execution (local time)</span><strong>{formatDate(config.lastRunAtUtc)}</strong></div>
            <div className="summary-item"><span>Last result</span><strong>{config.lastRunStatus || '—'}</strong></div>
            {config.lastRunMessage && <p className="run-message">{config.lastRunMessage}</p>}
            <button className="secondary full" type="button" onClick={runNow} disabled={running || !config.isActive}>
              {running ? 'Queueing…' : 'Run now'}
            </button>
            <small className="hint">Run now queues the job for the next worker polling cycle.</small>
          </section>

          <section className="note">
            <div className="note-icon">i</div><div><strong>Operational note</strong><p>Keep this job enabled only when the attendance device/API connection and credentials are configured. The worker must be connected to the real Prowess attendance synchronization service.</p></div>
          </section>
        </aside>
      </div>
      <footer>PROWESS ERP <span>•</span> Scheduler Administration</footer>
    </main>
  )
}
